/**
 * Junior Mathematics Student Notes - Years 7-10
 * Based on Vanuatu National Syllabus
 * Comprehensive study materials for all strands
 */

// Year 7 Mathematics Student Notes
export const year7MathematicsStudentNotes = {
  strand1: {
    title: "Number",
    overview: "Apply effective strategies for numerical calculation and problem-solving",
    image: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400",
    units: [
      {
        id: "unit1.1",
        title: "Number and Operations",
        content: `
## Number and Operations

### Integers
**Integers** are whole numbers that can be positive, negative, or zero.
- Positive integers: 1, 2, 3, 4, ...
- Negative integers: -1, -2, -3, -4, ...
- Zero: 0

### Rules for Integer Operations

**Addition:**
- Same signs: Add and keep the sign
- Different signs: Subtract and take the sign of the larger number

**Examples:**
- 5 + 3 = 8
- (-5) + (-3) = -8
- 5 + (-3) = 2
- (-5) + 3 = -2

**Multiplication and Division:**
- Same signs → Positive result
- Different signs → Negative result

**Examples:**
- 4 × 3 = 12
- (-4) × (-3) = 12
- 4 × (-3) = -12
- (-12) ÷ 4 = -3

### Order of Operations (BODMAS)
1. **B**rackets
2. **O**rders (powers and roots)
3. **D**ivision and **M**ultiplication (left to right)
4. **A**ddition and **S**ubtraction (left to right)

**Example:** 3 + 4 × 2 = 3 + 8 = 11 (multiply first!)

### Rounding Numbers
- **To nearest 10:** Look at units digit (5+ round up)
- **To nearest 100:** Look at tens digit
- **Decimal places:** Count digits after decimal point

**Example:** Round 3.847 to 2 decimal places = 3.85
`,
        keyTerms: [
          { term: "Integer", definition: "A whole number (positive, negative, or zero)" },
          { term: "BODMAS", definition: "Order of operations: Brackets, Orders, Division/Multiplication, Addition/Subtraction" },
          { term: "Rounding", definition: "Approximating a number to a given degree of accuracy" },
          { term: "Significant figures", definition: "The meaningful digits in a number" },
          { term: "Estimation", definition: "Finding an approximate answer" }
        ],
        practiceQuestions: [
          "Calculate: -8 + 5 - (-3)",
          "Evaluate: 24 ÷ 4 + 3 × 2",
          "Round 4.567 to 1 decimal place"
        ],
        examTips: ["Always show BODMAS steps clearly", "Check your sign when multiplying/dividing"]
      },
      {
        id: "unit1.2",
        title: "Fractions, Percentages, Ratio and Proportion",
        content: `
## Fractions, Decimals and Percentages

### Converting Between Forms

| Fraction | Decimal | Percentage |
|----------|---------|------------|
| 1/2 | 0.5 | 50% |
| 1/4 | 0.25 | 25% |
| 3/4 | 0.75 | 75% |
| 1/5 | 0.2 | 20% |
| 1/10 | 0.1 | 10% |

### Converting Methods
- **Fraction to Decimal:** Divide numerator by denominator
- **Decimal to Percentage:** Multiply by 100
- **Percentage to Decimal:** Divide by 100
- **Fraction to Percentage:** (Numerator ÷ Denominator) × 100

### Operations with Fractions

**Addition/Subtraction:** Make denominators the same first
$$\\frac{1}{4} + \\frac{2}{3} = \\frac{3}{12} + \\frac{8}{12} = \\frac{11}{12}$$

**Multiplication:** Multiply tops and bottoms
$$\\frac{2}{3} × \\frac{4}{5} = \\frac{8}{15}$$

**Division:** Flip the second fraction and multiply
$$\\frac{2}{3} ÷ \\frac{4}{5} = \\frac{2}{3} × \\frac{5}{4} = \\frac{10}{12} = \\frac{5}{6}$$

### Percentages
**Finding a percentage of an amount:**
30% of 80 = 0.30 × 80 = 24

**Percentage increase/decrease:**
- Increase by 20%: Multiply by 1.20
- Decrease by 15%: Multiply by 0.85

### Ratio
- Simplify by dividing by common factors
- 12:8 = 3:2 (divide both by 4)

**Sharing in a ratio:**
Share 60 in ratio 2:3
- Total parts = 2 + 3 = 5
- One part = 60 ÷ 5 = 12
- Answer: 24 and 36
`,
        keyTerms: [
          { term: "Fraction", definition: "A number written as one integer over another" },
          { term: "Numerator", definition: "The top number in a fraction" },
          { term: "Denominator", definition: "The bottom number in a fraction" },
          { term: "Percentage", definition: "A fraction out of 100" },
          { term: "Ratio", definition: "A comparison of quantities using division" }
        ],
        practiceQuestions: [
          "Convert 3/8 to a decimal and percentage",
          "Calculate 2/5 + 3/4",
          "Share 120 in the ratio 3:5"
        ],
        examTips: ["Always simplify fractions", "Show conversion steps clearly"]
      }
    ]
  },
  strand2: {
    title: "Algebra",
    overview: "Use algebraic expressions to represent and solve problems",
    image: "https://images.unsplash.com/photo-1596495578065-6e0763fa1178?w=400",
    units: [
      {
        id: "unit2.1",
        title: "Algebraic Expressions",
        content: `
## Algebraic Expressions

### Key Vocabulary
- **Variable:** A letter representing an unknown number (x, y, n)
- **Coefficient:** The number in front of a variable (in 3x, coefficient is 3)
- **Constant:** A number on its own (in 2x + 5, constant is 5)
- **Term:** Parts separated by + or - (2x + 3y has two terms)
- **Expression:** A collection of terms (2x + 3y - 4)

### Algebraic Conventions
- Write 1x as just x
- Write a × b as ab
- Write a ÷ b as a/b
- Variables in alphabetical order: 2ab not 2ba

### Simplifying Expressions
Collect **like terms** (same letter, same power)

**Examples:**
- 3x + 5x = 8x
- 4a + 2b + 3a = 7a + 2b
- 5xy - 2xy + y = 3xy + y

### Substitution
Replace variables with given values

**Example:** If a = 3 and b = 2, find 2a + 3b
2(3) + 3(2) = 6 + 6 = 12

### Expanding Brackets
Multiply everything inside by what's outside

**Examples:**
- 3(x + 4) = 3x + 12
- 2(3a - 5) = 6a - 10
- -4(2x - 3) = -8x + 12 (watch the signs!)
`,
        keyTerms: [
          { term: "Variable", definition: "A letter representing an unknown value" },
          { term: "Coefficient", definition: "The number multiplied by a variable" },
          { term: "Like terms", definition: "Terms with the same variable and power" },
          { term: "Expression", definition: "A combination of terms without an equals sign" },
          { term: "Substitution", definition: "Replacing variables with given values" }
        ],
        practiceQuestions: [
          "Simplify: 4x + 3y - 2x + 5y",
          "If p = 4 and q = -2, find 3p - 2q",
          "Expand: 5(2x - 3)"
        ],
        examTips: ["Be careful with negative signs when expanding", "Only combine like terms"]
      },
      {
        id: "unit2.2",
        title: "Solving Equations",
        content: `
## Solving Equations

### What is an Equation?
An equation has an **equals sign** and can be solved to find the unknown.

### Solving by Inverse Operations
Do the **opposite** operation to both sides:
- Addition ↔ Subtraction
- Multiplication ↔ Division

### One-Step Equations
**Example 1:** x + 5 = 12
x = 12 - 5 = 7

**Example 2:** 3x = 15
x = 15 ÷ 3 = 5

### Two-Step Equations
**Example:** 2x + 3 = 11
Step 1: 2x = 11 - 3 = 8
Step 2: x = 8 ÷ 2 = 4

### Equations with Unknown on Both Sides
**Example:** 5x + 2 = 3x + 10
Step 1: 5x - 3x = 10 - 2
Step 2: 2x = 8
Step 3: x = 4

### Checking Solutions
Substitute your answer back into the original equation.
Check: 5(4) + 2 = 22 and 3(4) + 10 = 22 ✓

### Forming Equations
"I think of a number, multiply by 3 and add 5. The answer is 20."
Equation: 3n + 5 = 20
Solution: n = 5
`,
        keyTerms: [
          { term: "Equation", definition: "A statement with an equals sign showing two expressions are equal" },
          { term: "Solution", definition: "The value of the variable that makes the equation true" },
          { term: "Inverse operation", definition: "The opposite operation (+ and -, × and ÷)" },
          { term: "Balance method", definition: "Doing the same operation to both sides" }
        ],
        practiceQuestions: [
          "Solve: x - 7 = 15",
          "Solve: 4x + 5 = 21",
          "Solve: 3x + 4 = x + 12"
        ],
        examTips: ["Always check your answer by substituting back", "Show each step clearly"]
      }
    ]
  },
  strand3: {
    title: "Measurement",
    overview: "Estimate and calculate measurements in various contexts",
    image: "https://images.unsplash.com/photo-1503023345310-bd7c1de61c7d?w=400",
    units: [
      {
        id: "unit3.1",
        title: "Perimeter, Area and Volume",
        content: `
## Perimeter, Area and Volume

### Perimeter
The **distance around** a shape.

**Rectangle:** P = 2(length + width) = 2l + 2w
**Square:** P = 4 × side = 4s

### Area of 2D Shapes

| Shape | Formula |
|-------|---------|
| Rectangle | A = length × width = lw |
| Square | A = side² = s² |
| Triangle | A = ½ × base × height = ½bh |
| Parallelogram | A = base × height = bh |

### Units of Area
- cm² (square centimetres)
- m² (square metres)
- 1 m² = 10,000 cm²

### Volume of 3D Shapes
**Volume** is the space inside a 3D shape.

| Shape | Formula |
|-------|---------|
| Cube | V = side³ = s³ |
| Cuboid | V = length × width × height = lwh |

### Units of Volume
- cm³ (cubic centimetres)
- m³ (cubic metres)
- 1 m³ = 1,000,000 cm³
- 1 litre = 1000 cm³

### Compound Shapes
Break into simple shapes, find each area, then add (or subtract).

**Example:** L-shaped room
Split into two rectangles, find each area, add together.
`,
        keyTerms: [
          { term: "Perimeter", definition: "The total distance around a shape" },
          { term: "Area", definition: "The amount of surface covered by a shape" },
          { term: "Volume", definition: "The amount of 3D space inside a solid shape" },
          { term: "Compound shape", definition: "A shape made from simpler shapes" }
        ],
        practiceQuestions: [
          "Find the perimeter and area of a rectangle 8cm by 5cm",
          "Calculate the area of a triangle with base 12cm and height 7cm",
          "Find the volume of a cuboid 4cm × 5cm × 3cm"
        ],
        examTips: ["Include units in your answer", "Draw diagrams and label them"]
      }
    ]
  },
  strand4: {
    title: "Geometry",
    overview: "Understand geometric reasoning and spatial features",
    image: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400",
    units: [
      {
        id: "unit4.1",
        title: "Angles and Lines",
        content: `
## Angles and Lines

### Types of Angles
| Type | Size |
|------|------|
| Acute | Less than 90° |
| Right | Exactly 90° |
| Obtuse | Between 90° and 180° |
| Straight | Exactly 180° |
| Reflex | Between 180° and 360° |

### Angle Facts
- **Angles on a straight line** add to 180°
- **Angles around a point** add to 360°
- **Vertically opposite angles** are equal

### Parallel Lines
When a line crosses two parallel lines:

| Angle Type | Property |
|------------|----------|
| Corresponding | Equal (F-shape) |
| Alternate | Equal (Z-shape) |
| Co-interior | Add to 180° (C-shape) |

### Angles in Polygons
- **Triangle:** Angles add to 180°
- **Quadrilateral:** Angles add to 360°
- **Pentagon:** Angles add to 540°

**Interior angles of n-sided polygon:** (n - 2) × 180°
`,
        keyTerms: [
          { term: "Parallel lines", definition: "Lines that never meet, always the same distance apart" },
          { term: "Perpendicular", definition: "Lines that meet at 90°" },
          { term: "Corresponding angles", definition: "Equal angles in the same position on parallel lines" },
          { term: "Alternate angles", definition: "Equal angles on opposite sides of a transversal" },
          { term: "Transversal", definition: "A line that crosses two or more other lines" }
        ],
        practiceQuestions: [
          "Find the missing angle if two angles on a straight line are 65° and x°",
          "Two parallel lines are cut by a transversal. One angle is 72°. Find all other angles.",
          "Find the sum of interior angles of a hexagon"
        ],
        examTips: ["Mark equal angles on diagrams", "State which angle fact you're using"]
      }
    ]
  },
  strand5: {
    title: "Probability and Statistics",
    overview: "Collect, organize, represent and analyze data; use probabilities",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400",
    units: [
      {
        id: "unit5.1",
        title: "Probability",
        content: `
## Probability

### Probability Scale
Probability is always between 0 and 1:
- **0** = Impossible
- **0.5** = Even chance
- **1** = Certain

### Calculating Probability
$$P(event) = \\frac{\\text{Number of favourable outcomes}}{\\text{Total number of outcomes}}$$

### Example: Rolling a Dice
- P(getting a 6) = 1/6
- P(getting an even number) = 3/6 = 1/2
- P(getting less than 5) = 4/6 = 2/3

### Listing Outcomes
Use systematic lists or tables.

**Two coins tossed:** HH, HT, TH, TT (4 outcomes)
P(two heads) = 1/4
P(at least one head) = 3/4
`,
        keyTerms: [
          { term: "Probability", definition: "The likelihood of an event happening (0 to 1)" },
          { term: "Outcome", definition: "A possible result of an experiment" },
          { term: "Event", definition: "A set of outcomes we're interested in" },
          { term: "Sample space", definition: "The set of all possible outcomes" },
          { term: "Theoretical probability", definition: "Probability calculated from known outcomes" }
        ],
        practiceQuestions: [
          "A bag contains 3 red, 5 blue and 2 green balls. Find P(red)",
          "What is the probability of rolling a prime number on a dice?",
          "List all outcomes when flipping two coins"
        ],
        examTips: ["Write probability as a fraction first, then simplify", "Always check probabilities are between 0 and 1"]
      },
      {
        id: "unit5.2",
        title: "Data and Statistics",
        content: `
## Data and Statistics

### Types of Data
- **Categorical:** Names/labels (colours, types)
- **Numerical:** Numbers (discrete or continuous)

### Averages
| Average | Method |
|---------|--------|
| Mean | Total ÷ number of values |
| Median | Middle value when ordered |
| Mode | Most common value |
| Range | Highest - Lowest |

### Example
Data: 3, 5, 7, 7, 8
- Mean = (3+5+7+7+8) ÷ 5 = 30 ÷ 5 = 6
- Median = 7 (middle value)
- Mode = 7 (appears most)
- Range = 8 - 3 = 5

### Representing Data

**Bar Chart:** Bars for each category
**Pictogram:** Pictures represent values
**Pie Chart:** Circle divided into sectors
**Line Graph:** Points connected with lines

### Frequency Tables
| Score | Frequency |
|-------|-----------|
| 1 | 4 |
| 2 | 7 |
| 3 | 5 |
| 4 | 3 |
| 5 | 1 |

Mean = (1×4 + 2×7 + 3×5 + 4×3 + 5×1) ÷ 20
`,
        keyTerms: [
          { term: "Mean", definition: "The total divided by the number of values" },
          { term: "Median", definition: "The middle value when data is ordered" },
          { term: "Mode", definition: "The most frequently occurring value" },
          { term: "Range", definition: "The difference between highest and lowest values" },
          { term: "Frequency", definition: "How often a value occurs" }
        ],
        practiceQuestions: [
          "Find the mean, median, mode and range of: 4, 6, 3, 8, 6, 5, 6",
          "Draw a bar chart for the frequency table above",
          "Calculate the mean from a frequency table"
        ],
        examTips: ["Order data before finding median", "Check your mean by estimation"]
      }
    ]
  }
};

// Year 8 Mathematics Student Notes
export const year8MathematicsStudentNotes = {
  strand1: {
    title: "Number",
    overview: "Apply effective strategies including indices and standard form",
    image: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400",
    units: [
      {
        id: "unit1.1",
        title: "Indices and Standard Form",
        content: `
## Indices (Powers)

### Index Notation
- 2³ means 2 × 2 × 2 = 8
- The small number is the **index** (or power/exponent)

### Index Laws
| Law | Rule | Example |
|-----|------|---------|
| Multiplication | aᵐ × aⁿ = aᵐ⁺ⁿ | 2³ × 2⁴ = 2⁷ |
| Division | aᵐ ÷ aⁿ = aᵐ⁻ⁿ | 5⁶ ÷ 5² = 5⁴ |
| Power of power | (aᵐ)ⁿ = aᵐⁿ | (3²)⁴ = 3⁸ |
| Zero power | a⁰ = 1 | 5⁰ = 1 |
| Negative power | a⁻ⁿ = 1/aⁿ | 2⁻³ = 1/8 |

### Standard Form (Scientific Notation)
A number written as: **a × 10ⁿ** where 1 ≤ a < 10

**Large numbers:** 4,500,000 = 4.5 × 10⁶
**Small numbers:** 0.00034 = 3.4 × 10⁻⁴

### Converting To/From Standard Form
- Count places the decimal moves
- Moving left: positive power
- Moving right: negative power
`,
        keyTerms: [
          { term: "Index (power)", definition: "The small number showing how many times to multiply" },
          { term: "Base", definition: "The number being multiplied" },
          { term: "Standard form", definition: "A number written as a × 10ⁿ where 1 ≤ a < 10" },
          { term: "Negative index", definition: "Indicates reciprocal (1 divided by the positive power)" }
        ],
        practiceQuestions: [
          "Simplify: 3⁴ × 3²",
          "Write 0.00056 in standard form",
          "Calculate: (2.5 × 10³) × (4 × 10²)"
        ],
        examTips: ["Remember: same base needed for index laws", "Check standard form has 1 ≤ a < 10"]
      }
    ]
  },
  strand2: {
    title: "Algebra",
    overview: "Expand and factorise expressions, solve equations",
    image: "https://images.unsplash.com/photo-1596495578065-6e0763fa1178?w=400",
    units: [
      {
        id: "unit2.1",
        title: "Expanding and Factorising",
        content: `
## Expanding Double Brackets

### FOIL Method
(a + b)(c + d) = ac + ad + bc + bd

**F**irst, **O**uter, **I**nner, **L**ast

### Example
(x + 3)(x + 5)
= x² + 5x + 3x + 15
= x² + 8x + 15

### Special Cases
- (x + a)² = x² + 2ax + a²
- (x - a)² = x² - 2ax + a²
- (x + a)(x - a) = x² - a² (Difference of squares)

## Factorising

### Taking Out Common Factors
6x + 9 = 3(2x + 3)
4x² - 8x = 4x(x - 2)

### Factorising Quadratics
x² + 7x + 12 = (x + 3)(x + 4)

**Method:** Find two numbers that:
- Multiply to give 12
- Add to give 7
Answer: 3 and 4
`,
        keyTerms: [
          { term: "Expand", definition: "Multiply out brackets" },
          { term: "Factorise", definition: "Write as a product of factors" },
          { term: "FOIL", definition: "First, Outer, Inner, Last method for expanding" },
          { term: "Common factor", definition: "A factor that divides into all terms" }
        ],
        practiceQuestions: [
          "Expand: (x + 4)(x - 2)",
          "Factorise: 12x - 8",
          "Factorise: x² + 5x + 6"
        ],
        examTips: ["Check expansion by substituting a value", "Check factorisation by expanding"]
      }
    ]
  },
  strand3: {
    title: "Measurement",
    overview: "Calculate with circles, prisms and compound measures",
    image: "https://images.unsplash.com/photo-1503023345310-bd7c1de61c7d?w=400",
    units: [
      {
        id: "unit3.1",
        title: "Circles",
        content: `
## Circle Measurements

### Key Parts
- **Radius (r):** Distance from centre to edge
- **Diameter (d):** Distance across through centre (d = 2r)
- **Circumference (C):** Distance around the circle
- **π (pi):** Approximately 3.14159...

### Formulas
**Circumference:** C = πd = 2πr
**Area:** A = πr²

### Example
Circle with radius 5cm:
- Circumference = 2 × π × 5 = 10π ≈ 31.4 cm
- Area = π × 5² = 25π ≈ 78.5 cm²

### Sectors and Arcs
**Arc length:** (θ/360) × 2πr
**Sector area:** (θ/360) × πr²

where θ is the angle in degrees
`,
        keyTerms: [
          { term: "Radius", definition: "Distance from centre to circumference" },
          { term: "Diameter", definition: "Distance across circle through centre" },
          { term: "Circumference", definition: "The perimeter of a circle" },
          { term: "Sector", definition: "A 'slice' of a circle" },
          { term: "Arc", definition: "Part of the circumference" }
        ],
        practiceQuestions: [
          "Find the circumference of a circle with diameter 14cm",
          "Find the area of a circle with radius 6cm",
          "Find the arc length of a sector with angle 60° and radius 9cm"
        ],
        examTips: ["Leave answers in terms of π unless told otherwise", "Use diameter for circumference, radius for area"]
      }
    ]
  },
  strand4: {
    title: "Geometry",
    overview: "Pythagoras' theorem and transformations",
    image: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400",
    units: [
      {
        id: "unit4.1",
        title: "Pythagoras' Theorem",
        content: `
## Pythagoras' Theorem

### The Theorem
In a **right-angled triangle**:
**c² = a² + b²**

where c is the **hypotenuse** (longest side, opposite the right angle)

### Finding the Hypotenuse
If a = 3 and b = 4:
c² = 3² + 4² = 9 + 16 = 25
c = √25 = 5

### Finding a Shorter Side
If c = 13 and a = 5:
b² = c² - a² = 169 - 25 = 144
b = √144 = 12

### Pythagorean Triples
Common combinations:
- 3, 4, 5
- 5, 12, 13
- 8, 15, 17
- 7, 24, 25

### Applications
- Finding distances
- Checking if angles are right angles
- Finding heights and lengths in real life
`,
        keyTerms: [
          { term: "Hypotenuse", definition: "The longest side of a right-angled triangle, opposite the right angle" },
          { term: "Pythagoras' theorem", definition: "In a right triangle: c² = a² + b²" },
          { term: "Pythagorean triple", definition: "Three whole numbers that satisfy Pythagoras' theorem" }
        ],
        practiceQuestions: [
          "Find the hypotenuse if the other sides are 6cm and 8cm",
          "Find the missing side if the hypotenuse is 10cm and one side is 6cm",
          "Is a triangle with sides 9, 40, 41 right-angled?"
        ],
        examTips: ["Always identify the hypotenuse first", "Square root at the end only"]
      }
    ]
  },
  strand5: {
    title: "Probability and Statistics",
    overview: "Combined events and scatter graphs",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400",
    units: [
      {
        id: "unit5.1",
        title: "Combined Events",
        content: `
## Combined Probabilities

### Sample Space Diagrams
Lists all outcomes for combined events.

**Two dice rolled:**
36 possible outcomes (6 × 6)

### Tree Diagrams
Show probabilities along branches.
- Multiply along branches
- Add between branches

### Rules
**AND (both events):** Multiply probabilities
P(A and B) = P(A) × P(B)

**OR (either event):** Add probabilities
P(A or B) = P(A) + P(B) (if mutually exclusive)

### Example
Bag with 3 red, 2 blue balls. Two picks with replacement.
P(both red) = 3/5 × 3/5 = 9/25
P(at least one blue) = 1 - P(both red) = 16/25
`,
        keyTerms: [
          { term: "Sample space", definition: "All possible outcomes of an experiment" },
          { term: "Tree diagram", definition: "A diagram showing probabilities of combined events" },
          { term: "Independent events", definition: "Events where one doesn't affect the other" },
          { term: "Mutually exclusive", definition: "Events that cannot happen at the same time" }
        ],
        practiceQuestions: [
          "Draw a sample space diagram for two dice and find P(total = 7)",
          "Use a tree diagram to find P(at least one head) when tossing two coins",
          "Two cards drawn with replacement from a pack. Find P(both hearts)"
        ],
        examTips: ["Label tree diagrams clearly", "Check branches from same point add to 1"]
      }
    ]
  }
};

// Year 9 Mathematics Student Notes
export const year9MathematicsStudentNotes = {
  strand1: {
    title: "Number",
    overview: "Bounds, standard form calculations and compound interest",
    image: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400",
    units: [
      {
        id: "unit1.1",
        title: "Upper and Lower Bounds",
        content: `
## Bounds and Accuracy

### Rounding and Bounds
When a measurement is rounded, the actual value lies within bounds.

**Example:** Length = 5.3 cm (to 1 d.p.)
- Lower bound = 5.25 cm
- Upper bound = 5.35 cm
- We write: 5.25 ≤ x < 5.35

### Error Intervals
The lower bound is **inclusive** (≤)
The upper bound is **exclusive** (<)

### Calculations with Bounds
| Operation | Maximum | Minimum |
|-----------|---------|---------|
| A + B | Upper A + Upper B | Lower A + Lower B |
| A - B | Upper A - Lower B | Lower A - Upper B |
| A × B | Upper A × Upper B | Lower A × Lower B |
| A ÷ B | Upper A ÷ Lower B | Lower A ÷ Upper B |
`,
        keyTerms: [
          { term: "Upper bound", definition: "The highest value before rounding up to the next value" },
          { term: "Lower bound", definition: "The lowest value that rounds to the given value" },
          { term: "Error interval", definition: "The range of possible values" },
          { term: "Significant figures", definition: "The meaningful digits in a number" }
        ],
        practiceQuestions: [
          "A length is 12cm to the nearest cm. Find the bounds.",
          "If a = 5.4 (1 d.p.) and b = 3.2 (1 d.p.), find bounds of a × b",
          "A rectangle has dimensions 8.5m × 4.5m (to 1 d.p.). Find the maximum area."
        ],
        examTips: ["Always check if bounds are for maximum or minimum calculation"]
      }
    ]
  },
  strand2: {
    title: "Algebra",
    overview: "Quadratic equations and simultaneous equations",
    image: "https://images.unsplash.com/photo-1596495578065-6e0763fa1178?w=400",
    units: [
      {
        id: "unit2.1",
        title: "Quadratic Equations",
        content: `
## Solving Quadratic Equations

### Standard Form
ax² + bx + c = 0

### Method 1: Factorising
x² + 5x + 6 = 0
(x + 2)(x + 3) = 0
x = -2 or x = -3

### Method 2: Quadratic Formula
$$x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}$$

**Example:** 2x² + 5x - 3 = 0
a = 2, b = 5, c = -3
x = (-5 ± √(25 + 24)) ÷ 4
x = (-5 ± 7) ÷ 4
x = 0.5 or x = -3

### Method 3: Completing the Square
x² + 6x + 5 = 0
(x + 3)² - 9 + 5 = 0
(x + 3)² = 4
x + 3 = ±2
x = -1 or x = -5
`,
        keyTerms: [
          { term: "Quadratic equation", definition: "An equation with x² as the highest power" },
          { term: "Root/solution", definition: "The values of x that satisfy the equation" },
          { term: "Discriminant", definition: "b² - 4ac, determines the nature of roots" },
          { term: "Completing the square", definition: "Writing in the form (x + p)² + q" }
        ],
        practiceQuestions: [
          "Solve by factorising: x² - 7x + 12 = 0",
          "Use the quadratic formula to solve: 3x² + 2x - 5 = 0",
          "Solve by completing the square: x² + 8x + 7 = 0"
        ],
        examTips: ["Always try factorising first", "If using formula, write out a, b, c clearly"]
      },
      {
        id: "unit2.2",
        title: "Simultaneous Equations",
        content: `
## Simultaneous Equations

### Two Unknowns, Two Equations
We need two equations to find two unknowns.

### Method 1: Elimination
3x + 2y = 13 ... (1)
2x + 2y = 10 ... (2)

Subtract: x = 3
Substitute: 6 + 2y = 10, so y = 2

### Method 2: Substitution
y = 2x + 1 ... (1)
3x + y = 11 ... (2)

Substitute (1) into (2):
3x + (2x + 1) = 11
5x = 10, x = 2
Then y = 5

### Graphical Method
- Draw both lines
- The intersection point is the solution
`,
        keyTerms: [
          { term: "Simultaneous equations", definition: "Two or more equations solved together" },
          { term: "Elimination", definition: "Removing one variable by adding/subtracting equations" },
          { term: "Substitution", definition: "Replacing one variable with an expression" }
        ],
        practiceQuestions: [
          "Solve: 2x + 3y = 7 and x + y = 3",
          "Solve: y = x + 2 and 2x + y = 8",
          "Solve graphically: x + y = 5 and y = 2x - 1"
        ],
        examTips: ["Choose the method that suits the equations", "Always substitute back to check"]
      }
    ]
  },
  strand3: {
    title: "Measurement",
    overview: "3D shapes and similar shapes",
    image: "https://images.unsplash.com/photo-1503023345310-bd7c1de61c7d?w=400",
    units: [
      {
        id: "unit3.1",
        title: "Volume and Surface Area",
        content: `
## 3D Shapes

### Prisms
**Volume** = Area of cross-section × Length

### Cylinders
**Volume:** V = πr²h
**Surface Area:** SA = 2πr² + 2πrh

### Cones
**Volume:** V = ⅓πr²h
**Surface Area:** SA = πr² + πrl (l = slant height)

### Spheres
**Volume:** V = ⁴⁄₃πr³
**Surface Area:** SA = 4πr²

### Pyramids
**Volume:** V = ⅓ × Base Area × Height

### Similar Shapes
If scale factor = k:
- Lengths scale by k
- Areas scale by k²
- Volumes scale by k³
`,
        keyTerms: [
          { term: "Prism", definition: "A 3D shape with constant cross-section" },
          { term: "Cylinder", definition: "A prism with circular cross-section" },
          { term: "Cone", definition: "A 3D shape with circular base tapering to a point" },
          { term: "Sphere", definition: "A perfectly round 3D shape" },
          { term: "Scale factor", definition: "The ratio of corresponding lengths in similar shapes" }
        ],
        practiceQuestions: [
          "Find the volume of a cone with radius 4cm and height 9cm",
          "Find the surface area of a sphere with radius 5cm",
          "Two similar solids have volumes 27cm³ and 125cm³. Find the ratio of their heights."
        ],
        examTips: ["Learn the formulas - they may not be given", "Remember volume uses r³ for spheres"]
      }
    ]
  },
  strand4: {
    title: "Geometry",
    overview: "Trigonometry and vectors",
    image: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400",
    units: [
      {
        id: "unit4.1",
        title: "Trigonometry",
        content: `
## Trigonometric Ratios

### SOHCAHTOA
| Ratio | Formula |
|-------|---------|
| Sin θ | Opposite / Hypotenuse |
| Cos θ | Adjacent / Hypotenuse |
| Tan θ | Opposite / Adjacent |

### Finding a Side
**Example:** Find x if angle = 35°, hypotenuse = 10
x/10 = sin 35°
x = 10 × sin 35° = 5.74

### Finding an Angle
**Example:** Find θ if opposite = 5, adjacent = 8
tan θ = 5/8
θ = tan⁻¹(5/8) = 32.0°

### Common Values
| Angle | Sin | Cos | Tan |
|-------|-----|-----|-----|
| 30° | 0.5 | 0.866 | 0.577 |
| 45° | 0.707 | 0.707 | 1 |
| 60° | 0.866 | 0.5 | 1.732 |
`,
        keyTerms: [
          { term: "Sine", definition: "Ratio of opposite to hypotenuse in a right triangle" },
          { term: "Cosine", definition: "Ratio of adjacent to hypotenuse" },
          { term: "Tangent", definition: "Ratio of opposite to adjacent" },
          { term: "Hypotenuse", definition: "The longest side, opposite the right angle" },
          { term: "SOHCAHTOA", definition: "Memory aid for trig ratios" }
        ],
        practiceQuestions: [
          "Find the opposite side if angle = 40° and hypotenuse = 15cm",
          "Find the angle if opposite = 7 and hypotenuse = 12",
          "A ladder 5m long leans against a wall at 70° to the ground. How high up the wall does it reach?"
        ],
        examTips: ["Label sides O, A, H before choosing ratio", "Check calculator is in degree mode"]
      }
    ]
  },
  strand5: {
    title: "Probability and Statistics",
    overview: "Cumulative frequency and box plots",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400",
    units: [
      {
        id: "unit5.1",
        title: "Cumulative Frequency",
        content: `
## Cumulative Frequency

### Definition
The running total of frequencies.

### Cumulative Frequency Table
| Class | Frequency | Cumulative Frequency |
|-------|-----------|---------------------|
| 0-10 | 5 | 5 |
| 10-20 | 12 | 17 |
| 20-30 | 18 | 35 |
| 30-40 | 10 | 45 |
| 40-50 | 5 | 50 |

### Finding Median and Quartiles
From the curve:
- **Median (Q2):** at n/2
- **Lower Quartile (Q1):** at n/4
- **Upper Quartile (Q3):** at 3n/4
- **IQR:** Q3 - Q1

### Box Plots
Shows: minimum, Q1, median, Q3, maximum

**Interquartile Range (IQR):** Q3 - Q1 = middle 50% of data
`,
        keyTerms: [
          { term: "Cumulative frequency", definition: "The running total of frequencies" },
          { term: "Median", definition: "The middle value (50th percentile)" },
          { term: "Quartile", definition: "Values dividing data into quarters" },
          { term: "Interquartile range", definition: "Q3 - Q1, the spread of the middle 50%" },
          { term: "Box plot", definition: "A diagram showing the five-number summary" }
        ],
        practiceQuestions: [
          "Draw a cumulative frequency curve for the data above",
          "Find the median and IQR from your curve",
          "Draw a box plot from the five-number summary: 12, 25, 32, 41, 55"
        ],
        examTips: ["Plot cumulative frequency at upper class boundary", "Read values carefully from curves"]
      }
    ]
  }
};

// Year 10 Mathematics Student Notes (abbreviated for space)
export const year10MathematicsStudentNotes = {
  strand1: {
    title: "Number",
    overview: "Surds and advanced calculations",
    image: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400",
    units: [
      {
        id: "unit1.1",
        title: "Surds",
        content: `
## Surds

### What is a Surd?
A surd is a root that cannot be simplified to a rational number.
Examples: √2, √3, √5 are surds
√4 = 2 is NOT a surd (it's rational)

### Simplifying Surds
√12 = √(4 × 3) = √4 × √3 = 2√3
√50 = √(25 × 2) = 5√2

### Rules for Surds
- √a × √b = √(ab)
- √a ÷ √b = √(a/b)
- √a × √a = a

### Adding and Subtracting
Only combine LIKE surds:
3√2 + 5√2 = 8√2
√8 + √18 = 2√2 + 3√2 = 5√2

### Rationalising the Denominator
Remove surds from the bottom:
1/√3 = 1/√3 × √3/√3 = √3/3

For (a + √b): multiply by (a - √b)
1/(2 + √3) = (2 - √3)/((2 + √3)(2 - √3)) = (2 - √3)/(4 - 3) = 2 - √3
`,
        keyTerms: [
          { term: "Surd", definition: "A root that cannot be simplified to a rational number" },
          { term: "Rationalise", definition: "Remove surds from the denominator" },
          { term: "Like surds", definition: "Surds with the same number under the root" },
          { term: "Conjugate", definition: "Expression with opposite sign between terms" }
        ],
        practiceQuestions: [
          "Simplify: √72",
          "Simplify: √12 + √27 - √3",
          "Rationalise: 6/(√5 - 1)"
        ],
        examTips: ["Always simplify surds fully", "Check rationalisation by multiplying out"]
      }
    ]
  },
  strand2: {
    title: "Algebra",
    overview: "Advanced equations and functions",
    image: "https://images.unsplash.com/photo-1596495578065-6e0763fa1178?w=400",
    units: [
      {
        id: "unit2.1",
        title: "The Discriminant",
        content: `
## The Discriminant

### Definition
For ax² + bx + c = 0, the discriminant is:
**D = b² - 4ac**

### What the Discriminant Tells Us
| D Value | Nature of Roots |
|---------|-----------------|
| D > 0 | Two distinct real roots |
| D = 0 | One repeated real root |
| D < 0 | No real roots |

### Example
2x² + 5x + 3 = 0
D = 25 - 24 = 1 > 0
∴ Two distinct real roots

### Using the Discriminant
To find values of k:
If x² + kx + 9 = 0 has equal roots:
D = k² - 36 = 0
k = ±6
`,
        keyTerms: [
          { term: "Discriminant", definition: "b² - 4ac, determines the nature of quadratic roots" },
          { term: "Distinct roots", definition: "Two different solutions" },
          { term: "Repeated root", definition: "One solution (graph touches x-axis)" },
          { term: "No real roots", definition: "No solutions (graph doesn't cross x-axis)" }
        ],
        practiceQuestions: [
          "Find the discriminant and describe roots: x² - 6x + 9 = 0",
          "For what values of k does kx² + 4x + 1 = 0 have no real roots?",
          "Show that 3x² + 2x + 5 = 0 has no real solutions"
        ],
        examTips: ["Use discriminant to check before factorising", "D = 0 means perfect square"]
      }
    ]
  },
  strand3: {
    title: "Measurement",
    overview: "Advanced area and volume calculations",
    image: "https://images.unsplash.com/photo-1503023345310-bd7c1de61c7d?w=400",
    units: [
      {
        id: "unit3.1",
        title: "Sectors and Segments",
        content: `
## Arc Length and Sector Area

### Arc Length
Arc length = (θ/360) × 2πr

### Sector Area
Sector area = (θ/360) × πr²

### Example
Sector with radius 8cm, angle 45°:
Arc = (45/360) × 2π × 8 = 2π cm
Area = (45/360) × π × 64 = 8π cm²

### Segment Area
Segment = Sector - Triangle
Segment = (θ/360)πr² - ½r²sinθ
`,
        keyTerms: [
          { term: "Arc", definition: "Part of the circumference" },
          { term: "Sector", definition: "Region bounded by two radii and an arc" },
          { term: "Segment", definition: "Region bounded by a chord and an arc" },
          { term: "Chord", definition: "A straight line joining two points on a circle" }
        ],
        practiceQuestions: [
          "Find the arc length of a sector with radius 10cm and angle 72°",
          "Find the area of a sector with radius 6cm and angle 120°",
          "Find the area of a segment with radius 8cm and angle 60°"
        ],
        examTips: ["Remember: θ must be in degrees for these formulas"]
      }
    ]
  },
  strand4: {
    title: "Geometry",
    overview: "Sine and cosine rules, circle theorems",
    image: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400",
    units: [
      {
        id: "unit4.1",
        title: "Sine and Cosine Rules",
        content: `
## Sine Rule

### Formula
a/sin A = b/sin B = c/sin C

### When to Use
- Two angles and one side
- Two sides and a non-included angle

### Example
Find a if A = 40°, B = 60°, b = 8
a/sin 40° = 8/sin 60°
a = 8 × sin 40°/sin 60° = 5.94

## Cosine Rule

### Formula
a² = b² + c² - 2bc cos A
or
cos A = (b² + c² - a²)/(2bc)

### When to Use
- Three sides (finding angle)
- Two sides and included angle (finding side)

### Area Formula
Area = ½ab sin C
`,
        keyTerms: [
          { term: "Sine rule", definition: "a/sin A = b/sin B = c/sin C" },
          { term: "Cosine rule", definition: "a² = b² + c² - 2bc cos A" },
          { term: "Included angle", definition: "The angle between two given sides" }
        ],
        practiceQuestions: [
          "Find side a in triangle ABC where A = 50°, B = 70°, b = 12cm",
          "Find angle A in triangle where a = 7, b = 8, c = 9",
          "Find the area of triangle with sides 5 and 7, included angle 65°"
        ],
        examTips: ["Draw and label the triangle first", "Choose sine rule for angles, cosine rule for sides"]
      }
    ]
  },
  strand5: {
    title: "Probability and Statistics",
    overview: "Histograms and statistical inference",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400",
    units: [
      {
        id: "unit5.1",
        title: "Histograms",
        content: `
## Histograms with Unequal Class Widths

### Key Concept
In histograms, AREA represents frequency, not height.

### Frequency Density
Frequency Density = Frequency ÷ Class Width

### Drawing a Histogram
1. Calculate class widths
2. Calculate frequency density for each class
3. Draw bars with heights = frequency density
4. No gaps between bars

### Example
| Class | Frequency | Width | F. Density |
|-------|-----------|-------|------------|
| 0-10 | 20 | 10 | 2 |
| 10-30 | 40 | 20 | 2 |
| 30-60 | 45 | 30 | 1.5 |

### Reading from Histograms
Frequency = Area = Width × Height
`,
        keyTerms: [
          { term: "Histogram", definition: "A chart where area represents frequency" },
          { term: "Frequency density", definition: "Frequency divided by class width" },
          { term: "Class width", definition: "The range of values in each class" },
          { term: "Continuous data", definition: "Data that can take any value in a range" }
        ],
        practiceQuestions: [
          "Calculate frequency densities for given data",
          "Draw a histogram for data with unequal class widths",
          "Estimate the frequency for a given range from a histogram"
        ],
        examTips: ["Always calculate frequency density", "Check: area should equal total frequency"]
      }
    ]
  }
};

export default {
  year7: year7MathematicsStudentNotes,
  year8: year8MathematicsStudentNotes,
  year9: year9MathematicsStudentNotes,
  year10: year10MathematicsStudentNotes
};
